import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { 
  Brain, 
  Zap,
  Target,
  Clock,
  Hash,
  AlertTriangle,
  CheckCircle,
  Activity,
  TrendingUp,
  Shield,
  Search,
  Calendar,
  ChevronDown,
  Eye,
  Sparkles,
  BarChart3,
  Info
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface RelevanceAnalysisData {
  id: string;
  postId: string;
  provider: string;
  model: string;
  rawScore: number;
  normalizedScore: number;
  analysisText: string;
  reasoning: string;
  keywordsFound: string[];
  keywordsMatched: string[];
  threatIndicators: string[];
  confidenceFactors: {
    sourceCredibility: number;
    contentSpecificity: number;
    technicalDepth: number;
    timelineRelevance: number;
  };
  fallbackReason?: string;
  processingTime: number;
  createdAt: string;
}

interface RelevanceAnalysisCardProps {
  postId: string;
  relevanceScore: number;
  expanded: boolean;
  onToggle: () => void;
}

export function RelevanceAnalysisCard({ postId, relevanceScore, expanded, onToggle }: RelevanceAnalysisCardProps) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<RelevanceAnalysisData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadAnalysis = async () => {
    if (analysis || loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.getRelevanceAnalysis(postId);
      setAnalysis(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load analysis';
      if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        setError('Detailed relevance analysis is not available for this post. The analysis may still be processing or was not generated when the post was created.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded && !analysis && !loading && !error) {
      loadAnalysis();
    }
  }, [expanded]);

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-red-600 bg-red-50 border-red-200';
    if (score >= 0.6) return 'text-orange-600 bg-orange-50 border-orange-200';
    if (score >= 0.4) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 0.8) return 'High Relevance';
    if (score >= 0.6) return 'Medium-High';
    if (score >= 0.4) return 'Medium';
    return 'Low Relevance';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.8) return { text: 'High', color: 'text-green-600' };
    if (confidence >= 0.6) return { text: 'Medium', color: 'text-yellow-600' };
    if (confidence >= 0.4) return { text: 'Low', color: 'text-orange-600' };
    return { text: 'Very Low', color: 'text-red-600' };
  };

  // Header section with score
  const renderHeader = () => (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center space-x-3">
        <div className={`px-3 py-1.5 rounded-full border text-sm font-medium ${getScoreColor(relevanceScore)}`}>
          <div className="flex items-center space-x-1.5">
            <Zap className="w-4 h-4" />
            <span>{(relevanceScore * 100).toFixed(0)}% Relevance</span>
          </div>
        </div>
        
        <div className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium border border-purple-200">
          {getScoreLabel(relevanceScore)}
        </div>
      </div>
      
      <button
        onClick={onToggle}
        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
      >
        <Eye className="w-4 h-4" />
        <span>{expanded ? 'Hide' : 'Show'} Relevance Details</span>
        <ChevronDown className={`w-4 h-4 transform transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
    </div>
  );

  // Loading state
  if (expanded && loading) {
    return (
      <>
        {renderHeader()}
        <div className="mt-6 pt-6 border-t space-y-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading relevance analysis...</span>
          </div>
        </div>
      </>
    );
  }

  // Error state with helpful message
  if (expanded && error) {
    return (
      <>
        {renderHeader()}
        <div className="mt-6 pt-6 border-t space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <Info className="h-5 w-5 text-blue-400 flex-shrink-0" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Relevance Analysis Information</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>{error}</p>
                  <p className="mt-2">The relevance score of <strong>{(relevanceScore * 100).toFixed(0)}%</strong> indicates this post's importance for threat intelligence based on content analysis.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Main render
  return (
    <>
      {renderHeader()}
      
      {expanded && analysis && (
        <div className="mt-6 pt-6 border-t space-y-6">
          {/* AI Analysis Overview */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Brain className="w-5 h-5 text-purple-600" />
                <h4 className="text-sm font-semibold text-purple-900">AI Relevance Analysis</h4>
              </div>
              <div className="flex items-center space-x-2 text-xs">
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                  {analysis.provider}
                </span>
                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                  {analysis.model.split('/').pop()?.split('-').slice(0, 2).join('-') || analysis.model}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-4 h-4 text-purple-500" />
                <span className="text-gray-600">Raw Score:</span>
                <span className="font-medium">{analysis.rawScore}/15</span>
              </div>
              <div className="flex items-center space-x-2">
                <Activity className="w-4 h-4 text-blue-500" />
                <span className="text-gray-600">Normalized:</span>
                <span className="font-medium">{(analysis.normalizedScore * 100).toFixed(1)}%</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">Processing:</span>
                <span className="font-medium">{analysis.processingTime}ms</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">Analyzed:</span>
                <span className="font-medium">{formatDistanceToNow(new Date(analysis.createdAt), { addSuffix: true })}</span>
              </div>
            </div>
          </div>

          {/* Confidence Factors */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Target className="w-4 h-4 text-indigo-600" />
              <h4 className="text-sm font-medium text-gray-900">Confidence Factors</h4>
              <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">4 dimensions</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { label: 'Source Credibility', value: analysis.confidenceFactors.sourceCredibility, icon: Shield, description: 'Reliability of information source' },
                { label: 'Content Specificity', value: analysis.confidenceFactors.contentSpecificity, icon: Search, description: 'Level of technical detail' },
                { label: 'Technical Depth', value: analysis.confidenceFactors.technicalDepth, icon: Brain, description: 'Presence of IOCs and analysis' },
                { label: 'Timeline Relevance', value: analysis.confidenceFactors.timelineRelevance, icon: TrendingUp, description: 'Current threat relevance' }
              ].map((factor, index) => {
                const confidence = getConfidenceLabel(factor.value);
                const Icon = factor.icon;
                return (
                  <div key={index} className="bg-gray-50 border border-gray-200 p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Icon className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-900">{factor.label}</span>
                      </div>
                      <span className={`text-sm font-semibold ${confidence.color}`}>
                        {confidence.text}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-indigo-400 to-purple-500 transition-all duration-300"
                        style={{ width: `${Math.min(factor.value * 100, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{factor.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Keywords and Indicators */}
          {(analysis.keywordsFound.length > 0 || analysis.threatIndicators.length > 0) && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Hash className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-medium text-gray-900">Keywords & Threat Indicators</h4>
                <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                  {analysis.keywordsFound.length + analysis.threatIndicators.length} found
                </span>
              </div>
              
              {analysis.keywordsFound.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-600 mb-2">Security Keywords Detected:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.keywordsFound.map((keyword, index) => {
                      const isMatched = analysis.keywordsMatched.includes(keyword);
                      return (
                        <span
                          key={index}
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            isMatched 
                              ? 'bg-green-100 text-green-800 border border-green-200' 
                              : 'bg-gray-100 text-gray-700 border border-gray-200'
                          }`}
                        >
                          {isMatched && <CheckCircle className="w-3 h-3 mr-1" />}
                          {keyword}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {analysis.threatIndicators.length > 0 && (
                <div>
                  <p className="text-xs text-gray-600 mb-2">Threat Indicators Identified:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.threatIndicators.map((indicator, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-red-100 text-red-800 font-medium border border-red-200"
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {indicator}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Analysis Reasoning */}
          {analysis.reasoning && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-medium text-blue-900">Analysis Reasoning</h4>
              </div>
              <p className="text-sm text-blue-800 leading-relaxed">{analysis.reasoning}</p>
            </div>
          )}

          {/* Fallback Analysis Notice */}
          {analysis.fallbackReason && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-center">
                <Info className="h-4 w-4 text-yellow-600 mr-2 flex-shrink-0" />
                <div>
                  <span className="text-sm font-medium text-yellow-800">Note: </span>
                  <span className="text-sm text-yellow-700">{analysis.fallbackReason}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}